/**
 * The make-up air unit's settings, held for the session.
 *
 * A module store rather than component state because two things read it
 * that are not React: the unit's own readout down in the sub-fab (a canvas
 * texture repainted from the slow tick) and the dialog that opens when the
 * unit is clicked. Settings persist for the session only - the unit is a
 * thing to tinker with, not a saved recipe.
 *
 * The CONDITION is not the student's to set: it follows the house. When the
 * facilities station arms the loaded-filter event, the bus tells this store
 * and the bench derates the unit the technician is looking at.
 */

import {
  DEFAULT_MAU,
  NOMINAL_MAU_CONDITION,
  runMau,
  type MauCondition,
  type MauReport,
  type MauSettings,
} from './MauSim'

let settings: MauSettings = { ...DEFAULT_MAU }
let condition: MauCondition = { ...NOMINAL_MAU_CONDITION }
let report: MauReport = runMau(settings, condition)
const listeners = new Set<() => void>()

function recompute(): void {
  report = runMau(settings, condition)
  for (const fn of [...listeners]) fn()
}

export const mauStore = {
  get settings(): Readonly<MauSettings> {
    return settings
  },
  get condition(): Readonly<MauCondition> {
    return condition
  },
  /** The unit as it runs under the current settings, in its current condition. */
  get report(): MauReport {
    return report
  },
  set(patch: Partial<MauSettings>): void {
    settings = { ...settings, ...patch }
    recompute()
  },
  /** The house's business, not the bench's: what is wrong with the unit. */
  setCondition(next: MauCondition): void {
    condition = { ...next }
    recompute()
  },
  reset(): void {
    settings = { ...DEFAULT_MAU }
    recompute()
  },
  subscribe(fn: () => void): () => void {
    listeners.add(fn)
    return () => {
      listeners.delete(fn)
    }
  },
}
